'use client'

import React from 'react'
import { Typography } from '@repo/ui/typography'
import { Skeleton } from '@repo/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/tooltip'
import { Info } from 'lucide-react'
import { DistributionBar } from '../ui/DistributionBar'
import { useCountUp } from '../../hooks/useCountUp'
import { cn } from '@repo/ui/utils'
import { TableState } from '../ui/TableState'
import { useStaticOrganizationStats } from '../../hooks/useStaticOrganizations'

interface SpecimensChartProps {
  organizationId: string
}

interface SpecimenMetrics {
  totalSamples: number
  uniqueDonors: number
  uniqueDiseases: number
  specimenTypes: number
  sexDistribution: { [key: string]: number }
  specimenTypeDistribution: { [key: string]: number }
  ageStatistics: { mean_age: number; std_dev_age: number }
  countriesOfOrigin: number
}

interface MetricCardProps {
  value: number
  label: string
  format?: 'number' | 'age'
  className?: string
  customValue?: string
}

interface DistributionSectionProps {
  title: string
  data: { [key: string]: number }
  colorTheme: 'sex' | 'specimen'
  tooltip?: string
}


const MetricCard: React.FC<MetricCardProps> = ({ value, label, format = 'number', className = '', customValue }) => {
  const count = useCountUp(value, { duration: 1000, delay: 0 })

  const displayValue = customValue || (format === 'age' ? `${count.count.toFixed(1)}` : count.count.toLocaleString())

  return (
    <div
      className={cn(
        'border border-border bg-white rounded-lg flex flex-col items-center overflow-hidden pb-2 pt-1',
        className
      )}
    >
      <div className="text-2xl font-bold text-mint-700 mb-1">{displayValue}</div>
      <div className="text-sm text-grey-600">{label}</div>
    </div>
  )
}

const DistributionSection: React.FC<DistributionSectionProps> = ({ title, data, colorTheme, tooltip }) => {
  if (Object.keys(data).length === 0) return null

  const total = Object.values(data).reduce((sum, count) => sum + count, 0)

  return (
    <div className="bg-white rounded-lg py-6">
      <div className="flex items-center gap-2 mb-3">
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Typography font="fragment" className="text-gray-900">
                  {title}
                </Typography>
                <Info size={14} />
              </div>
            </TooltipTrigger>
            <TooltipContent
              className=" bg-mint-200 text-mint-900 shadow-xl border-0 rounded-lg px-3 py-2 max-w-xs"
              side="bottom"
              align="start"
              sideOffset={4}
            >
              <Typography size="sm" weight="medium" className="text-mint-900 leading-relaxed">
                {tooltip}
              </Typography>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Typography font="fragment" className="text-gray-900">
            {title}
          </Typography>
        )}
      </div>

      <DistributionBar
        data={data}
        total={total}
        height={12}
        colorTheme={colorTheme}
        animate={true}
        animationDelay={0}
      />
    </div>
  )
}

const processMetrics = (orgStats?: any): SpecimenMetrics => {
  if (!orgStats) {
    return {
      totalSamples: 0,
      uniqueDonors: 0,
      uniqueDiseases: 0,
      specimenTypes: 0,
      sexDistribution: {},
      specimenTypeDistribution: {},
      ageStatistics: { mean_age: 0, std_dev_age: 0 },
      countriesOfOrigin: 0,
    }
  }

  const specimenTypes = Object.keys(orgStats.specimen_type_distribution || {}).length

  return {
    totalSamples: orgStats.total_unique_specimens || 0,
    uniqueDonors: orgStats.total_unique_donors || 0,
    uniqueDiseases: orgStats.total_unique_diagnoses || 0,
    specimenTypes,
    sexDistribution: orgStats.sex_distribution || {},
    specimenTypeDistribution: orgStats.specimen_type_distribution || {},
    ageStatistics: orgStats.age_statistics || { mean_age: 0, std_dev_age: 0 },
    countriesOfOrigin: orgStats.unique_countries_of_origin?.length || 0,
  }
}

interface MetricsGridProps {
  metrics: SpecimenMetrics
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ metrics }) => {
  const meanAgeCount = useCountUp(metrics.ageStatistics.mean_age, { duration: 1000, delay: 0 })
  const stdDevAgeCount = useCountUp(metrics.ageStatistics.std_dev_age, { duration: 1000, delay: 0 })

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mb-4">
        <MetricCard value={metrics.totalSamples} label="Total Samples" />
        <MetricCard value={metrics.uniqueDonors} label="Unique Donors" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <MetricCard value={metrics.uniqueDiseases} label="Unique Diseases" />
        <MetricCard value={metrics.countriesOfOrigin} label="Countries of Origin" />
        <MetricCard
          value={metrics.ageStatistics.mean_age}
          label="Mean Age ± Std Dev"
          customValue={`${meanAgeCount.count.toFixed(1)} ± ${stdDevAgeCount.count.toFixed(1)}`}
        />
      </div>
    </>
  )
}

export default function SpecimensChart({ organizationId }: SpecimensChartProps) {
  const { stats: orgStats, isLoading, error } = useStaticOrganizationStats(organizationId)

  const metrics: SpecimenMetrics = React.useMemo(() => processMetrics(orgStats), [orgStats])

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="text-center text-grey-500 py-8">
          <Skeleton className="h-8 w-64 mx-auto mb-4" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    )
  }

  if (error) {
    console.error('Organization API error:', error)
    return (
      <div className="w-full">
        <TableState
          title="Unable to load statistics"
          description="There was an error loading the organization statistics. Please try again later."
          lottieSrc="/animations/connectivity-issues.json"
          lottieStyle={{ width: '250px', height: '200px' }}
        />
      </div>
    )
  }

  if (metrics.totalSamples === 0) {
    return (
      <div className="w-full">
        <TableState title="No specimen data available" description="There are no specimens..." />
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <Typography font="fragment" className="text-gray-900 mb-2">
          Specimen Statistics
        </Typography>
      </div>

      <MetricsGrid metrics={metrics} />

      <DistributionSection
        title="Sex Distribution"
        data={metrics.sexDistribution}
        colorTheme="sex"
        tooltip="Sex distribution is calculated per-specimen, not per-donor. A single donor may contribute multiple specimens."
      />

      <DistributionSection
        title="Specimen Type Distribution"
        data={metrics.specimenTypeDistribution}
        colorTheme="specimen"
      />
    </div>
  )
}
